// src/oura/merchant/Work.tsx
//
// Work: what we are doing for the merchant, at /oura/work. One sentence
// carries the next go-live (and whether it moved, and why), or how many
// pieces of work wait on them. Three tiles: this month's count, the next
// report, and the pages published. Then this month's work in groups by where
// each piece is, the latest actions, and on the right who to ask, what they
// asked, and the plan one line a month.
//
// "Ask for a change" is the one way a merchant starts a request (YW, 28 Sep
// 2026), and it has one button: on "Your account manager", which names who
// it goes to. "Your requests" sits under it: what they sent that way and the
// calls they asked for, With us open and Done folded. Each row opens its
// drawer, which holds the full text, the files and our reply
// (RequestDrawer), and offers "Ask a follow-up".
//
// `?request=<id>` is the bell's link on "{name} replied to your request". It
// unfolds "Your requests" (and its Done group when the request is done) and
// opens that request, then comes off the URL (with replace, so a refresh or
// Back does not repeat it). A request not in the list shows who replied and
// where the full reply is. Where the list is not served, "Your account
// manager" unfolds instead, with the same note.
//
// NOTHING HERE IS COMPUTED FROM TODAY'S DATE. `currentMonth` is the server's
// number, from the same anniversary rule Home uses. The one exception is a
// request's small change, which is "this month's" only in the Malaysian
// calendar month it was filed in (myRequests.ts).

import { CheckIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { CalendarDaysIcon, ChevronRightIcon, ClipboardListIcon, FileTextIcon, HistoryIcon, MessageSquarePlusIcon, PenLineIcon, UserRoundIcon } from "lucide-react";
import { canRequestChange, malaysianMonthNow, type ClientPagePayload, type ClientPlanItem, type ClientPlanStatus, type MerchantSchedule, type Thread } from "@service/aeo/clientReview";
import {
    ArrowLink,
    Bento,
    BentoStack,
    CardEmpty,
    Dot,
    FOCUS_RING,
    FoldCard,
    FoldScope,
    GroupFold,
    ICON_SQUARE_TONE,
    LATE_TEXT,
    ListRow,
    MERCHANT_STATUS,
    MERCHANT_STATUS_ORDER,
    OfValue,
    PageHead,
    PillButton,
    SURFACE,
    ShowMore,
    Steps,
    Tag,
    TAP_TARGET,
    Tile,
    Tiles,
    TYPE,
    returnFocusChain,
    ROW_HOVER_TIGHT,
    useFoldOpener,
} from "@oura/ui";
import { cn } from "../lib/cn";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { dayDate } from "../lib/oura";
import { useMyChangeRequests } from "../hooks/useMyChangeRequests";
import { ActionRow } from "./ActionRow";
import { ACCOUNT_MANAGER_TITLE, askFact, asksOf, isOpen, type Ask } from "./asks";
import { extraSmallChanges, myRequestGroup, myRequestRows, noticeFromState, type ReplyNote } from "./myRequests";
import { LinkedReplyNote, YourRequestsCard } from "./YourRequests";
import { ChangeRequestDrawer, type ChangeAbout } from "./drawers/ChangeRequestDrawer";
import { DatesDrawer } from "./drawers/DatesDrawer";
import { DidDrawer } from "./drawers/DidDrawer";
import { MonthDrawer } from "./drawers/MonthDrawer";
import { PagesDrawer } from "./drawers/PagesDrawer";
import { PlanDrawer } from "./drawers/PlanDrawer";
import { PhoneFoldCard } from "./PhoneFold";
import { RequestDrawer } from "./drawers/RequestDrawer";
import { WorkDrawer } from "./drawers/WorkDrawer";
import { comingUp, type ComingDate, lineDescription, lineWhen, liveSlip, planLineTitle, measuredItems, monthRows, openAskFor, pagesOnQuotation, phaseOf, planDone, planSpanText, sortPlanItems, workSentence } from "./model";
import { StartAnswering } from "./StartAnswering";
import { WorkCard } from "./WorkList";
import { calendarDay, shortDay, weekday } from "./workDates";
import { DateChip } from "./WorkMarks";
import { useMerchantDrawers } from "./useMerchantDrawers";
import { CLIENT_HINT } from "../nav";
import { clientArea, CLIENT_PAGE_SENTENCE, MERCHANT_V2 } from "../merchantV2";

/** Latest actions shown before "Show N more". */
const ACTIONS_SHOWN = 3;

/** Where a group of this month's work starts: what moves now open, the rest folded. */
const PLAN_OPEN = new Set(["waiting_on_you", "in_progress", "with_google"]);

type WorkDrawerState =
    | { kind: "dates" }
    /** Your plan: every date and every piece of work, month by month (the same drawer Documents opens). */
    | { kind: "plan" }
    | { kind: "pages" }
    /** The plan line's key: its code, or Delivery's key where a code repeats. */
    | { kind: "work"; key: string }
    | { kind: "month"; key: string }
    | { kind: "did"; id: string }
    | { kind: "request"; id: string }
    | null;

/** A bento stack whose cards join the page's one column below 1024px, so one of them can be put first. */
const PHONE_FLAT = "max-lg:contents max-lg:[&>*]:min-w-0";

export function Work({
    payload,
    beside,
}: {
    payload: ClientPagePayload;
    /** On Home: the card that sits beside Scheduled, at the top of these sections (How AI names you). */
    beside?: ReactNode;
}) {
    const { openQueue, openQuestion, openReport } = useMerchantDrawers();
    const [drawer, setDrawer] = useState<WorkDrawerState>(null);
    const [change, setChange] = useState<{ open: boolean; about: ChangeAbout | null }>({ open: false, about: null });
    const close = () => setDrawer(null);
    // Called from the component that renders the FoldScope, so it names it.
    const openCard = useFoldOpener("merchant-work");

    // What the business sent with Ask for a change. Not read on a staffer's
    // preview (the route is the merchant's own), nor where Ask for a change
    // does not show; `notServed` then, and the card stays off the page.
    const { read: requestsRead, refresh: refreshRequests } = useMyChangeRequests(canRequestChange() && !payload.previewedByStaff);
    const showRequests = requestsRead.state !== "notServed";
    const requestRows = requestsRead.state === "ready" ? myRequestRows(requestsRead.requests, requestsRead.calls) : null;

    // `?request=<id>` from the bell: read once, then taken off the URL. The
    // list is read again, since the reply that rang the bell may be newer.
    const [searchParams, setSearchParams] = useSearchParams();
    const { state: navState, hash, key: navKey } = useLocation();
    const requestParam = searchParams.get("request");
    // `?line=<key>` from Home's month card: that line opens in the list. Read
    // once, then taken off the URL like `?request=`, so a refresh does not repeat it.
    const [lineParam] = useState(() => searchParams.get("line"));
    useEffect(() => {
        if (!searchParams.has("line")) return;
        setSearchParams(
            (params) => {
                params.delete("line");
                return params;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams]);
    // `#your-requests` from Home's "See all requests" (3 Nov 2026): the card unfolds on a
    // phone, and the page lands on it. Scrolled from here, not ClientPage: coming from
    // Home the navigation is a transition, and ClientPage's scroll runs before this page
    // is on screen, so it finds no card. This runs once Work is there, once a visit.
    const revealRequests = hash === `#${REQUESTS_ANCHOR}`;
    useEffect(() => {
        if (!revealRequests) return;
        return landOn(REQUESTS_ANCHOR);
    }, [revealRequests, navKey]);
    /** `waiting`: the read this link started is not back yet. The list already on screen may predate the reply. */
    const [linked, setLinked] = useState<{ id: string; notice: ReplyNote; handled: boolean; waiting: boolean } | null>(null);
    useEffect(() => {
        if (!requestParam) return;
        const id = requestParam;
        setLinked({ id, notice: noticeFromState(navState), handled: false, waiting: true });
        void refreshRequests().finally(() => setLinked((l) => (l && l.id === id && l.waiting ? { ...l, waiting: false } : l)));
        setSearchParams(
            (params) => {
                params.delete("request");
                return params;
            },
            { replace: true },
        );
    }, [requestParam, navState, refreshRequests, setSearchParams]);
    // Once the list is read: unfold the card (and its Done group) and open the
    // request. Not in the list, or no list to show: the note says who replied.
    useEffect(() => {
        if (!linked || linked.handled) return;
        if (!showRequests) {
            openCard("contact");
            setLinked({ ...linked, handled: true });
            return;
        }
        if (requestsRead.state === "loading" || linked.waiting) return;
        openCard("requests");
        const row = requestRows?.find((r) => r.id === linked.id);
        if (row) {
            if (myRequestGroup(row) === "done") openCard("requests.done");
            setDrawer({ kind: "request", id: row.id });
        }
        setLinked({ ...linked, handled: true });
    }, [linked, showRequests, requestsRead.state, requestRows, openCard]);

    const { plan, subscription, schedule, stats } = payload;
    // No plan and no subscription: nothing has been sold yet (#3710), so there
    // is no month of work to count.
    const items = plan && subscription ? plan.items : [];
    const growth = plan?.phase === "growth";
    const phase = phaseOf(plan);
    const dates = comingUp(payload);
    // Over measured work only (staging 3fdc24e6); the list below still shows every line.
    const measured = measuredItems(items);
    const done = planDone(measured);
    const months = schedule ? monthRows(schedule) : [];
    // Growth counts every page on the quotation this term; the Foundation build
    // counts its own pages. The server's count from the page slots (H29) wins
    // when it sends one; until then, the quote's lines and the publishes.
    const onQuote = subscription?.pagesOnQuote ?? null;
    const pageUnits = onQuote ? onQuote.total : pagesOnQuotation(payload.engagement?.deliverables, subscription?.pageUnits ?? 0, growth);
    const pagesPublished = onQuote ? onQuote.done : growth && subscription ? subscription.pagesPublished : stats.pages.published;

    const asks = asksOf(payload.packs);
    const threadOf = (itemId: string) => payload.threads.find((t) => t.itemId === itemId) ?? null;
    // "1 small change a month" is said only where the contract carries it (G5).
    // No engagement means nothing is sold yet, so no such promise either.
    const smallChangeInPlan = payload.engagement?.deliverables.some((d) => d.code === "G5") ?? false;
    const golive = dates.find((d) => d.kind === "golive");
    const goliveAsk = golive?.itemId && asks.some((a) => a.item.id === golive.itemId && isOpen(a.item)) ? golive.itemId : null;
    const sentence = workSentence(items, dates);

    const workItem = drawer?.kind === "work" ? items.find((i) => (i.key ?? i.code) === drawer.key) ?? null : null;
    const monthRow = drawer?.kind === "month" ? months.find((r) => r.key === drawer.key) ?? null : null;
    const didEntry = drawer?.kind === "did" ? payload.activity.find((a) => a.id === drawer.id) ?? null : null;
    const requestRow = drawer?.kind === "request" ? requestRows?.find((r) => r.id === drawer.id) ?? null : null;
    const canAsk = canRequestChange();
    const smallChangeUsedAt =
        requestsRead.state === "ready"
            ? requestsRead.requests
                  .filter((r) => r.smallChange?.month === malaysianMonthNow())
                  .map((r) => r.at)
                  .sort()[0] ?? null
            : null;
    // In the redesign the form sits in the account manager card; "Ask a
    // follow-up" on a request fills it with that request (`change.about`).
    const changeProps = {
        businessId: payload.business.id,
        manager: payload.preparedBy,
        email: payload.contactEmail ?? null,
        viewer: payload.viewer.name,
        smallChangeInPlan,
        smallChangeUsedAt,
    };
    // A follow-up is written under the reply it follows, so the form on the
    // left is always a new request (16 Oct 2026).
    const followUpForm = (about: ChangeAbout, close: () => void) => <ChangeRequestDrawer inline open about={about} onClose={close} {...changeProps} />;
    const changeForm = canAsk ? (
        <ChangeRequestDrawer
            inline
            cancelable
            open
            about={null}
            onClose={() => setChange({ open: false, about: null })}
            businessId={payload.business.id}
            manager={payload.preparedBy}
            email={payload.contactEmail ?? null}
            viewer={payload.viewer.name}
            smallChangeInPlan={smallChangeInPlan}
            smallChangeUsedAt={smallChangeUsedAt}
        />
    ) : null;

    // The cards the page is made of, laid out below one way with the redesign
    // and another without it.
    const actionsCard = (
        <FoldCard
            id="actions"
            title="Latest actions"
            icon={{ icon: HistoryIcon, area: clientArea("work") }}
            foldedFact={dayDate(payload.activity[0]?.at) ? `Latest ${dayDate(payload.activity[0]?.at)}` : undefined}
        >
            {payload.activity.length === 0 ? (
                <CardEmpty icon={HistoryIcon} title="No work logged yet">
                    Every piece of work we do for you shows up here as it happens.
                </CardEmpty>
            ) : (
                <>
                    <div>
                        {payload.activity.slice(0, ACTIONS_SHOWN).map((a) => (
                            <ActionRow key={a.id} entry={a} onOpen={() => setDrawer({ kind: "did", id: a.id })} />
                        ))}
                    </div>
                    <ShowMore count={payload.activity.length - ACTIONS_SHOWN}>
                        {payload.activity.slice(ACTIONS_SHOWN).map((a) => (
                            <ActionRow key={a.id} entry={a} onOpen={() => setDrawer({ kind: "did", id: a.id })} />
                        ))}
                    </ShowMore>
                </>
            )}
        </FoldCard>
    );
    const contactCard = (
        <ContactCard
            payload={payload}
            canAsk={canAsk}
            replyNote={linked && !showRequests ? linked.notice : null}
            onAsk={() => setChange({ open: true, about: null })}
        />
    );
    const requestsCard = requestsRead.state !== "notServed" ? (
        <YourRequestsCard
            read={requestsRead}
            rows={requestRows}
            onRetry={() => void refreshRequests()}
            onOpen={(id) => setDrawer({ kind: "request", id })}
            manager={payload.preparedBy?.trim() || null}
            email={payload.contactEmail?.trim() || null}
            // Linked and not in the list: who replied, and where the reply is.
            missingNotice={
                linked?.handled && (requestsRead.state === "failed" || (requestRows !== null && !requestRows.some((r) => r.id === linked.id)))
                    ? linked.notice
                    : null
            }
            // A row opens where it is rather than in a
            // drawer (#4062 MR-FE-7). The drawer still
            // serves the switch-off path and ?request=.
            bare={MERCHANT_V2}
            untitled={MERCHANT_V2}
            inPlace={
                MERCHANT_V2
                    ? {
                        viewer: payload.viewer.name,
                        canFollowUp: canAsk && !payload.previewedByStaff,
                        onFollowUp: (about) => setChange({ open: true, about }),
                        renderFollowUp: MERCHANT_V2 ? followUpForm : undefined,
                    }
                    : undefined
            }
        />
    ) : null;
    const termCard = (
        <FoldCard
            id="plan"
            anchor="plan"
            title={MERCHANT_V2 ? "Scheduled" : "Your plan"}
            icon={{ icon: CalendarDaysIcon, area: clientArea("dates") }}
            // The redesign says the month inside the card, beside how many are left.
            right={schedule && !MERCHANT_V2 ? `Month ${schedule.currentMonth} of ${schedule.months.length}` : undefined}
        >
            {schedule?.changed && (
                // What moved, first: it is the only thing in this card that is news.
                <button
                    type="button"
                    onClick={() => setDrawer({ kind: "dates" })}
                    title={schedule.changed.summary}
                    className={cn(TYPE.tc, "mb-4 flex w-full items-center gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-left text-amber-900 hover:bg-amber-100", FOCUS_RING)}
                >
                    <PenLineIcon aria-hidden size={14} className="shrink-0" />
                    {/* 2 lines, never cut mid-word on one. */}
                    <span className="line-clamp-2 min-w-0 flex-1 break-words">
                        <span className="font-medium">Dates moved.</span> {schedule.changed.summary}
                    </span>
                    <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-amber-700" />
                </button>
            )}
            {schedule && MERCHANT_V2 ? (
                <TwelveMonths schedule={schedule} dates={dates} onAll={() => setDrawer({ kind: "dates" })} />
            ) : schedule ? (
                <Steps
                    items={months.map((r) => ({
                        key: r.key,
                        title: (
                            <>
                                {r.title}
                                {r.state === "now" && <span className={cn(TYPE.tm, "font-medium text-blue-600")}> · You are here</span>}
                            </>
                        ),
                        state: r.state,
                        onClick: () => setDrawer({ kind: "month", key: r.key }),
                    }))}
                />
            ) : (
                // Absent and null are the same answer: nothing has been
                // published, so there is no schedule to show.
                // A started plan is waiting on the publish, not on the start (#4379).
                subscription?.startedAt ? (
                    <CardEmpty icon={CalendarDaysIcon} title="Your dates are not published yet">
                        Your account manager publishes the dates for the whole term, and they appear here. You will be told whenever one of them moves.
                    </CardEmpty>
                ) : (
                    <CardEmpty icon={CalendarDaysIcon} title="Your dates are not set yet">
                        Once your plan starts we publish the dates for the whole term, and they appear here. You will be told whenever one of them moves.
                    </CardEmpty>
                )
            )}
        </FoldCard>
    );

    return (
        <FoldScope value="merchant-work">
            {/* "Our work", the business name alone, and one fixed sentence.
                The phase tag and the go-live line leave the head: a stage
                name is our word for where a plan is, and the dates it was
                summarising are on the cards below (#4062 MR-FE-3). No button
                here -- the questions live on To do. */}
            {/* Reached from Home's links (19 Oct 2026): the business name,
                "Our work" and its one fixed sentence. */}
            {MERCHANT_V2 ? (
                <PageHead over={payload.business.name} title="Our work" sentence={CLIENT_PAGE_SENTENCE.work} />
            ) : (
                <PageHead
                    over={
                        <>
                            {payload.business.name}
                            {phase && (
                                <>
                                    {" · "}
                                    <span className="inline-flex items-center gap-1.5">
                                        {phase.label}
                                        <Tag tone={phase.tone}>{phase.stage}</Tag>
                                    </span>
                                </>
                            )}
                        </>
                    }
                    title="Work"
                    sentence={CLIENT_HINT.work}
                    status={
                        sentence.kind === "golive" ? (
                            <>
                                {sentence.lead}
                                {sentence.moved && (
                                    <>
                                        , <span className={sentence.moved.late ? LATE_TEXT : undefined}>{sentence.moved.text}</span>
                                    </>
                                )}
                                .{sentence.reason && ` ${sentence.reason}`}
                            </>
                        ) : (
                            sentence.text
                        )
                    }
                    actions={<StartAnswering startId={goliveAsk} />}
                />
            )}
            <div className="space-y-5">
                {/* No "This month" tile: the card of that name sits right under
                    it and says the same count ("2 of 6 done"). Home keeps the tile. */}
                {/* On Home in the redesign: the next report is in Your 12 months
                    and the work count in Home's own row, so no tiles here. */}
                {!MERCHANT_V2 && (
                <Tiles cols={2} compactOnPhone>
                    <NextReportTile dates={dates} onOpen={() => setDrawer({ kind: "dates" })} />
                    <Tile compactOnPhone
                        label="Pages published"
                        icon={{ icon: FileTextIcon, area: clientArea("work") }}
                        value={pageUnits > 0 ? <OfValue n={pagesPublished} of={pageUnits} /> : "Not set"}
                        sub={
                            stats.coverage
                                // The Coverage Set: how many of the 50 questions the site itself answers.
                                ? `Your site answers ${stats.coverage.answered} of ${stats.coverage.total}`
                                : pageUnits > 0
                                    ? growth ? "This term" : "In your Foundation build"
                                    : "Set when your build is agreed"
                        }
                        onClick={() => setDrawer({ kind: "pages" })}
                        ariaLabel="Your pages: what is live and what comes next"
                    />
                </Tiles>
                )}

                {/* Below 1024px the two stacks dissolve into one column, and Your
                    account manager (with Ask for a change) comes first: on a
                    phone it was about 1,500px down, under This month. */}
                {MERCHANT_V2 ? (
                    // One page, two columns, in Home's and To do's cards (27 Oct
                    // 2026): the work and your requests on the left, where the
                    // reading is; the dates and who to ask in a narrow rail.
                    // On a phone the rail drops under the work, requests last.
                    <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
                        <div className={cn("flex min-w-0 flex-col gap-5", PHONE_FLAT)}>
                            {linked && !showRequests && (
                                <LinkedReplyNote notice={linked.notice} manager={payload.preparedBy?.trim() || null} email={payload.contactEmail?.trim() || null} />
                            )}
                            <WorkCard
                                items={items}
                                schedule={schedule}
                                asks={asks}
                                threadOf={threadOf}
                                engagement={payload.engagement}
                                onAnswer={openQueue}
                                openKey={lineParam}
                            />
                            {/* Shown when they can ask, even before their requests can be read. */}
                            {(requestsCard || canAsk) && (
                                <RequestsCard
                                    manager={payload.preparedBy?.trim() || null}
                                    canAsk={canAsk}
                                    composing={change.open && !change.about}
                                    reveal={revealRequests}
                                    onAsk={() => setChange({ open: true, about: null })}
                                    form={changeForm}
                                    withUs={requestRows ? requestRows.filter((r) => myRequestGroup(r) === "with_us").length : null}
                                    className="max-lg:order-last"
                                >
                                    {requestsCard}
                                </RequestsCard>
                            )}
                        </div>
                        {/* The account manager and their note are Home's (27 Oct
                            2026): this page had the same card a second time. */}
                        <div className={cn("flex min-w-0 flex-col gap-5", PHONE_FLAT)}>
                            {beside}
                            <ComingUpCard schedule={schedule} dates={dates} started={Boolean(subscription?.startedAt)} onAll={() => setDrawer({ kind: "plan" })} onChanged={() => setDrawer({ kind: "plan" })} />
                        </div>
                    </div>
                ) : (
                <Bento layout="c2">
                    <BentoStack className={PHONE_FLAT}>
                        {MERCHANT_V2 ? (
                            <PlanList
                                items={items}
                                asks={asks}
                                threadOf={threadOf}
                                engagement={payload.engagement}
                                onAnswer={openQueue}
                            />
                        ) : (
                            <FoldCard
                                id="this-month"
                                anchor="this-month"
                                // Month 1 is the Foundation build, and it has always been called that.
                                title={growth ? "This month" : "Your build"}
                                icon={{ icon: ClipboardListIcon, area: clientArea("work") }}
                                foldedFact={items.length === 0 ? undefined : measured.length > 0 ? `${done} of ${measured.length} done` : "By hand"}
                            >
                                {items.length === 0 ? (
                                    <CardEmpty icon={ClipboardListIcon} title="No work scheduled yet">Your account manager is preparing your plan. This page fills in the moment it is activated.</CardEmpty>
                                ) : (
                                    // The groups are the only children of this box, so the first loses its divider.
                                    <div>
                                        {MERCHANT_STATUS_ORDER.map((status) => {
                                            const list = sortPlanItems(items).filter((i) => i.status === status);
                                            if (list.length === 0) return null;
                                            const style = MERCHANT_STATUS[status];
                                            // Folded by hand, the head still says how many of its questions are late.
                                            const late = status === "waiting_on_you"
                                                ? list.filter((i) => {
                                                    const ask = asks.find((a) => a.item.id === openAskFor(i, asks));
                                                    return ask ? askFact(ask, threadOf(ask.item.id))?.late : false;
                                                }).length
                                                : 0;
                                            return (
                                                <GroupFold
                                                    key={status}
                                                    id={`this-month.${status}`}
                                                    lead="dot"
                                                    tone={style.tone}
                                                    title={style.label}
                                                    count={list.length}
                                                    defaultFolded={!PLAN_OPEN.has(status)}
                                                    note={late > 0 ? <span className={LATE_TEXT}>{late} late</span> : undefined}
                                                >
                                                    <div>
                                                        {list.map((item) => {
                                                            const askId = status === "waiting_on_you" ? openAskFor(item, asks) : null;
                                                            const ask = askId ? asks.find((a) => a.item.id === askId) ?? null : null;
                                                            const fact = ask ? askFact(ask, threadOf(ask.item.id)) : null;
                                                            return (
                                                                <PlanRow
                                                                    key={item.key ?? item.code}
                                                                    item={item}
                                                                    askId={askId}
                                                                    fact={fact}
                                                                    onOpen={() => (askId ? openQueue(askId) : setDrawer({ kind: "work", key: item.key ?? item.code }))}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </GroupFold>
                                            );
                                        })}
                                    </div>
                                )}
                        </FoldCard>
                        )}

                        {actionsCard}
                    </BentoStack>

                    <BentoStack className={PHONE_FLAT}>
                        <div className="min-w-0 max-lg:order-first">
                            {contactCard}
                        </div>
                        {requestsCard}
                        {termCard}
                    </BentoStack>
                </Bento>
                )}
            </div>

            <DatesDrawer
                open={drawer?.kind === "dates"}
                onClose={close}
                dates={dates}
                term={planSpanText(payload)}
                changed={schedule?.changed ?? null}
                onAnswer={(itemId) => {
                    close();
                    openQuestion(itemId);
                }}
            />
            <PlanDrawer open={drawer?.kind === "plan"} onClose={close} payload={payload} dates={dates} openKey={null} onAnswer={(askId) => {
                close();
                openQueue(askId);
            }} />
            <PagesDrawer
                open={drawer?.kind === "pages"}
                onClose={close}
                published={pagesPublished}
                units={pageUnits}
                pages={payload.pages}
                coverage={stats.coverage}
                coverageAbsent={stats.coverageAbsent}
            />
            <WorkDrawer key={workItem ? workItem.key ?? workItem.code : undefined} item={workItem} engagement={payload.engagement} onClose={close} />
            <MonthDrawer
                key={monthRow?.key}
                row={monthRow}
                dates={dates}
                reports={payload.reports}
                plan={subscription ? { name: subscription.name, ref: subscription.ref } : null}
                onOpenReport={(reportId) => {
                    close();
                    openReport(reportId);
                }}
                onClose={close}
            />
            <DidDrawer key={didEntry?.id} entry={didEntry} onClose={close} />
            <RequestDrawer
                key={requestRow?.id}
                row={requestRow}
                extra={requestRow?.kind === "change" && requestsRead.state === "ready" ? extraSmallChanges(requestsRead.requests).has(requestRow.id) : false}
                viewer={payload.viewer.name}
                manager={payload.preparedBy?.trim() || null}
                email={payload.contactEmail?.trim() || null}
                canFollowUp={canAsk && !payload.previewedByStaff}
                onFollowUp={(about) => {
                    close();
                    setChange({ open: true, about });
                }}
                onClose={close}
                returnFocus={requestRow ? returnFocusChain(`[data-request-id="${CSS.escape(requestRow.id)}"]`, `[data-group-head="requests.${myRequestGroup(requestRow)}"]`) : undefined}
            />
            {canAsk && !MERCHANT_V2 && (
                <ChangeRequestDrawer
                    open={change.open}
                    about={change.about}
                    onClose={() => setChange((c) => ({ ...c, open: false }))}
                    businessId={payload.business.id}
                    manager={payload.preparedBy}
                    email={payload.contactEmail ?? null}
                    viewer={payload.viewer.name}
                    smallChangeInPlan={smallChangeInPlan}
                    smallChangeUsedAt={smallChangeUsedAt}
                />
            )}
        </FoldScope>
    );
}

/**
 * The next report: when it comes, and whether it moved (in rose while the
 * move stands). It opens every date, in one list. The go-live is the page
 * sentence's.
 */
function NextReportTile({ dates, onOpen }: { dates: ReturnType<typeof comingUp>; onOpen: () => void }) {
    const next = dates.find((d) => d.kind === "report") ?? null;
    const icon = { icon: CalendarDaysIcon, area: clientArea("dates" as const) };
    const day = dayDate(next?.date);
    if (!next || !day) {
        return <Tile compactOnPhone label="Your next report" icon={icon} value="Not set yet" sub="Dates are published when your plan starts." onClick={onOpen} ariaLabel="Your next report is not set yet. Open your dates." />;
    }
    const slip = liveSlip(next);
    return (
        <Tile compactOnPhone
            label="Your next report"
            icon={icon}
            value={day}
            sub={slip ? <span className={LATE_TEXT}>{slip}</span> : "See every date"}
            onClick={onOpen}
            ariaLabel={`Your next report: ${day}${slip ? `, ${slip}` : ""}. Open your dates.`}
        />
    );
}

/**
 * One line of the month: its dot, its title, greyed when not started. The
 * group head says its status. A line waiting on an open question says that
 * question's date and opens it in the queue; every other line opens as work.
 */
/* ------------------------------------------------------------------------- *
 * The plan, flat (#4062 MR-FE-7)
 * ------------------------------------------------------------------------- */

/**
 * One flat list of the plan, with a Status filter, and rows that open where
 * they are.
 *
 * On a real staging client ELEVEN OF TWELVE LINES SAT INSIDE ONE "Not started"
 * fold, so the page about what we are doing showed one row of it. Folding by
 * status also put the answer to "what is left" behind the thing it was
 * answering. The list is flat now; the filter is there for the rarer question
 * ("show me just what needs me"), with each status's count beside it so
 * choosing one is not a guess.
 *
 * A row opens IN PLACE. The side drawer covered the page to show four short
 * lines, two of which repeated the row it came from.
 */
/**
 * The whole term at a glance, and the next three months with their dates
 * (#4062 MR-FE-7).
 *
 * The strip is the answer to "how far through are we", which the old card
 * made you count. Under it, only the months that are nearly here: the rest
 * open IN THE CARD rather than in a drawer, because a list of dates is not
 * worth covering the page for.
 */
/**
 * What is scheduled (16 Oct 2026): how far through the term, and the next
 * dates as tiles across the card's width, so neither side runs to white.
 * The full list is a press away, numbered, in Your dates. Nothing here is
 * worked out from today: the current month is the server's.
 */
function TwelveMonths({ schedule, dates, onAll }: { schedule: MerchantSchedule; dates: ComingDate[]; onAll: () => void }) {
    const cur = schedule.currentMonth;
    const total = schedule.months.length;
    const left = Math.max(0, total - cur);
    const next = dates.slice(0, 4);
    return (
        // No h-full: under a "Dates moved" note, 100% of the card plus the
        // note ran past the card, and "Every date" landed between two cards.
        <div className="flex flex-col">
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <p className="m-0 text-2xl font-semibold leading-none tabular-nums text-gray-900">
                    {left}
                    <span className={cn(TYPE.tb, "ml-1.5 font-normal text-gray-500")}>{left === 1 ? "month left" : "months left"}</span>
                </p>
                <span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>
                    Month {cur} of {total}
                </span>
            </div>
            <div className="mb-5 flex gap-[3px]" aria-hidden>
                {schedule.months.map((m) => (
                    <span
                        key={m.termMonth}
                        className={cn("h-1.5 min-w-0 flex-1 rounded-full", m.termMonth < cur ? "bg-blue-200" : m.termMonth === cur ? "bg-blue-600" : "bg-gray-200")}
                    />
                ))}
            </div>
            <p className={cn(TYPE.lab, "m-0 mb-2")}>Coming next</p>
            {next.length === 0 ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No more dates this term.</p>
            ) : (
                <ul className="list-plain m-0 grid grid-cols-1 gap-2 p-0 sm:grid-cols-2">
                    {next.map((d, i) => {
                        const slip = liveSlip(d);
                        return (
                            <li key={d.key}>
                                <button
                                    type="button"
                                    onClick={onAll}
                                    className={cn(
                                        "flex h-full w-full flex-col rounded-xl p-3 text-left transition-colors",
                                        i === 0 ? "bg-blue-50 hover:bg-blue-100" : "bg-gray-50 hover:bg-gray-100",
                                        FOCUS_RING,
                                    )}
                                >
                                    <span className={cn(TYPE.tc, "tabular-nums", i === 0 ? "font-medium text-blue-700" : "text-gray-500")}>{dayDate(d.date) ?? "Date to be set"}</span>
                                    <span className={cn(TYPE.tb, "mt-0.5 font-medium text-gray-900")}>{d.label}</span>
                                    {slip && <span className={cn(TYPE.tc, "mt-0.5", LATE_TEXT)}>{slip}</span>}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            <p className="m-0 mt-auto pt-3">
                <ArrowLink onClick={onAll}>Every date</ArrowLink>
            </p>
        </div>
    );
}

function PlanList({
    items,
    asks,
    threadOf,
    engagement,
    onAnswer,
}: {
    items: ClientPlanItem[];
    asks: Ask[];
    threadOf: (itemId: string) => Thread | null;
    engagement: ClientPagePayload["engagement"];
    onAnswer: (askId: string) => void;
}) {
    const [only, setOnly] = useState<ClientPlanStatus | "all">("all");
    const [openKey, setOpenKey] = useState<string | null>(null);

    // Today's order, with Done last: finished work is the least useful thing
    // on a page about what is happening.
    const ordered = useMemo(() => {
        const sorted = sortPlanItems(items);
        return [...sorted.filter((i) => i.status !== "done"), ...sorted.filter((i) => i.status === "done")];
    }, [items]);
    const counts = useMemo(() => {
        const out = {} as Record<ClientPlanStatus, number>;
        for (const st of MERCHANT_STATUS_ORDER) out[st] = items.filter((i) => i.status === st).length;
        return out;
    }, [items]);
    const shown = only === "all" ? ordered : ordered.filter((i) => i.status === only);

    return (
        <section className={cn(SURFACE.card, "p-4 sm:p-5")} id="this-month" data-anchor="this-month">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>What we are doing</h2>
                {items.length > 0 && (
                    MERCHANT_V2 ? (
                        // The house dropdown filter (style guide 6a): "All work"
                        // with its total first, then each status with its dot
                        // and count, and no status that has nothing in it.
                        <Select value={only} onValueChange={(v) => setOnly(v as ClientPlanStatus | "all")}>
                            <SelectTrigger aria-label="Filter by status" className="h-9 w-[180px] flex-shrink-0 text-sm">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All work <span className="text-gray-400">({items.length})</span>
                                </SelectItem>
                                {MERCHANT_STATUS_ORDER.filter((st) => counts[st] > 0).map((st) => (
                                    <SelectItem key={st} value={st}>
                                        <span className="inline-flex items-center gap-2">
                                            <Dot tone={MERCHANT_STATUS[st].tone} />
                                            {MERCHANT_STATUS[st].label} <span className="text-gray-400">({counts[st]})</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                    <label className={cn(TYPE.tc, "flex min-w-0 max-w-full items-center gap-2 text-gray-500")}>
                        Status
                        <select
                            className={cn("min-w-0 max-w-[60vw] truncate rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900", FOCUS_RING)}
                            value={only}
                            onChange={(e) => setOnly(e.target.value as ClientPlanStatus | "all")}
                        >
                            <option value="all">All {items.length}</option>
                            {MERCHANT_STATUS_ORDER.filter((st) => counts[st] > 0).map((st) => (
                                <option key={st} value={st}>
                                    {MERCHANT_STATUS[st].label} {counts[st]}
                                </option>
                            ))}
                        </select>
                    </label>
                    )
                )}
            </div>
            {items.length === 0 ? (
                <CardEmpty icon={ClipboardListIcon} title="No work scheduled yet">Your account manager is preparing your plan. This page fills in the moment it is activated.</CardEmpty>
            ) : shown.length === 0 ? (
                <CardEmpty
                    icon={ClipboardListIcon}
                    title="Nothing with this status"
                    action={
                        <PillButton size="sm" onClick={() => setOnly("all")}>
                            Show all work
                        </PillButton>
                    }
                >
                    Pick another status, or show everything in your plan.
                </CardEmpty>
            ) : (
                <div>
                    {shown.map((item) => {
                        const key = item.key ?? item.code;
                        const askId = item.status === "waiting_on_you" ? openAskFor(item, asks) : null;
                        const ask = askId ? asks.find((a) => a.item.id === askId) ?? null : null;
                        return (
                            <PlanLine
                                key={key}
                                item={item}
                                engagement={engagement}
                                fact={ask ? askFact(ask, threadOf(ask.item.id)) : null}
                                askId={askId}
                                open={openKey === key}
                                onToggle={() => setOpenKey((k) => (k === key ? null : key))}
                                onAnswer={onAnswer}
                            />
                        );
                    })}
                </div>
            )}
        </section>
    );
}

/** One plan line, and what it says when it is opened. */
function PlanLine({
    item,
    engagement,
    fact,
    askId,
    open,
    onToggle,
    onAnswer,
}: {
    item: ClientPlanItem;
    engagement: ClientPagePayload["engagement"];
    fact: ReturnType<typeof askFact>;
    askId: string | null;
    open: boolean;
    onToggle: () => void;
    onAnswer: (askId: string) => void;
}) {
    const status = MERCHANT_STATUS[item.status];
    const what = lineDescription(item, engagement);
    const when = lineWhen(item, engagement);
    return (
        <div className="border-t border-gray-100 first:border-t-0">
            <ListRow
                data-plan-code={item.code}
                lead="dot"
                stackMeta
                icon={<Dot tone={status.tone} />}
                title={planLineTitle(item)}
                srStatus={status.label}
                muted={status.muted}
                meta={<span className={cn(TYPE.tc, "text-gray-500")}>{status.label}</span>}
                onClick={onToggle}
            />
            {open && (
                // The redesign sets the detail in the same grey panel as
                // Setup's "How to connect?", so it reads as the row opened
                // rather than as more rows.
                <div className={MERCHANT_V2 ? "mb-3 ml-6 space-y-3 rounded-xl bg-gray-50 p-4" : "space-y-3 pb-3 pl-6 pr-1"}>
                    {what && <Block label="What it is">{what}</Block>}
                    {item.line && (
                        <Block label="Where it stands">
                            {item.line}
                            {item.month ? ` Part of month ${item.month}.` : ""}
                        </Block>
                    )}
                    {item.fixed && item.fixed.length > 0 && (
                        <Block label="What we fixed">
                            <ul className="list-plain m-0 p-0">
                                {item.fixed.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </Block>
                    )}
                    {when && <Block label="When">{when}</Block>}
                    {askId && (
                        <div className="flex justify-end">
                            <PillButton variant="primary" onClick={() => onAnswer(askId)}>
                                Answer now
                            </PillButton>
                        </div>
                    )}
                    {fact?.late && <p className={cn(TYPE.tc, "m-0", LATE_TEXT)}>{fact.text}</p>}
                </div>
            )}
        </div>
    );
}

/** A labelled paragraph inside an opened row: the drawer's block, in place. */
function Block({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0")}>{label}</p>
            <div className={cn(TYPE.tb, "mt-0.5 text-gray-700")}>{children}</div>
        </div>
    );
}

function PlanRow({
    item,
    askId,
    fact,
    onOpen,
}: {
    item: ClientPlanItem;
    askId: string | null;
    fact: ReturnType<typeof askFact>;
    onOpen: () => void;
}) {
    const status = MERCHANT_STATUS[item.status];
    return (
        <ListRow
            // Found again when the question's drawer closes.
            data-ask-id={askId ?? undefined}
            data-plan-code={item.code}
            lead="dot"
            stackMeta
            icon={<Dot tone={status.tone} />}
            title={planLineTitle(item)}
            srStatus={status.label}
            // Not started is greyed. Reported by hand and With Google are not:
            // neither is work that has not begun (ourteam-backend#4814).
            muted={status.muted}
            meta={fact && !fact.late ? fact.text : undefined}
            late={fact?.late ? fact.text : undefined}
            onClick={onOpen}
        />
    );
}

/**
 * Your requests, in a card of its own (27 Oct 2026): "Ask for a change" in
 * its head opens the form at the top of the list it adds to, and the list
 * reads as the conversation it is.
 */
const REQUESTS_ANCHOR = "your-requests";

/**
 * Scrolls to the element with this id and keeps it there while the page above it
 * fills in (its cards read their own data, so the page grows after the first paint:
 * a scroll made at once lands on a page too short to move). Lets go after 3 seconds,
 * or the moment they scroll, press a key or touch the page themselves. Returns the stop.
 */
function landOn(id: string): () => void {
    const scroller = document.getElementById(id)?.closest("main");
    const land = () => document.getElementById(id)?.scrollIntoView({ block: "start" });
    const frame = requestAnimationFrame(land);
    const grows = new ResizeObserver(land);
    Array.from(scroller?.children ?? []).forEach((child) => grows.observe(child));
    const stop = () => {
        cancelAnimationFrame(frame);
        grows.disconnect();
        clearTimeout(timer);
        USER_INPUT.forEach((type) => window.removeEventListener(type, stop, true));
    };
    const timer = window.setTimeout(stop, 3000);
    USER_INPUT.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
    return stop;
}
const USER_INPUT = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

function RequestsCard({
    manager,
    canAsk,
    composing,
    reveal,
    onAsk,
    form,
    withUs,
    className,
    children,
}: {
    manager: string | null;
    canAsk: boolean;
    composing: boolean;
    /** Landed on from Home's "See all requests": unfolded on a phone too. */
    reveal: boolean;
    onAsk: () => void;
    /** The new-request form, or null where it cannot send. */
    form: ReactNode;
    /** Requests with us, for the folded head on a phone; null until read. */
    withUs: number | null;
    className?: string;
    children: ReactNode;
}) {
    return (
        <PhoneFoldCard
            title="Your requests"
            anchor={REQUESTS_ANCHOR}
            foldedOnPhone
            // Ask for a change opens its form in this body: on a phone the card unfolds with it.
            openWhen={composing || reveal}
            foldedFact={withUs === null ? undefined : withUs > 0 ? `${withUs} with us` : "Nothing with us"}
            className={className}
            right={
                canAsk && form && !composing ? (
                    <PillButton variant="primary" size="sm" icon={MessageSquarePlusIcon} onClick={onAsk} className={TAP_TARGET}>
                        {manager ? `Ask ${manager} for a change` : "Ask for a change"}
                    </PillButton>
                ) : undefined
            }
        >
            {composing && form && <div className="rounded-xl bg-gray-50 p-4">{form}</div>}
            {children}
        </PhoneFoldCard>
    );
}

/** A moved date, said as how far it moved: "Moved 5 days later, was Fri 9 Oct". Null when it has not moved. */
function movedText(d: ComingDate): string | null {
    if (!liveSlip(d)) return null;
    const now = calendarDay(d.date);
    const was = calendarDay(d.was);
    if (!now || !was) return liveSlip(d);
    const days = Math.round((now.getTime() - was.getTime()) / 86_400_000);
    if (days === 0) return liveSlip(d);
    const n = Math.abs(days);
    return `Moved ${n} ${n === 1 ? "day" : "days"} ${days > 0 ? "later" : "earlier"}, was ${shortDay(was)}`;
}

/**
 * When things happen (27 Oct 2026): what moved, the next dates each on a
 * calendar tile, then how far through the term. The full list is a press
 * away, in Your plan. The current month is the server's.
 */
function ComingUpCard({
    schedule,
    dates,
    started,
    onAll,
    onChanged,
}: {
    schedule: MerchantSchedule | null | undefined;
    dates: ComingDate[];
    started: boolean;
    onAll: () => void;
    onChanged: () => void;
}) {
    const next = dates.slice(0, 4);
    const cur = schedule?.currentMonth ?? 0;
    const total = schedule?.months.length ?? 0;
    const left = Math.max(0, total - cur);
    return (
        <PhoneFoldCard
            anchor="plan"
            title="Coming up"
            foldedOnPhone
            foldedFact={next[0] ? `${dayDate(next[0].date) ?? "Date to be set"}: ${next[0].label}` : undefined}
            right={schedule ? <ArrowLink onClick={onAll}>Every date</ArrowLink> : undefined}
        >
            {schedule?.changed && (
                // What moved, first: it is the only thing in this card that is news.
                <button
                    type="button"
                    onClick={onChanged}
                    title={schedule.changed.summary}
                    className={cn(TYPE.tc, "flex w-full items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-left text-amber-900 hover:bg-amber-100", FOCUS_RING)}
                >
                    <PenLineIcon aria-hidden size={14} className="mt-0.5 shrink-0" />
                    <span className="line-clamp-3 min-w-0 flex-1 break-words">
                        <span className="font-medium">Dates moved.</span> {schedule.changed.summary}
                    </span>
                </button>
            )}
            {schedule ? (
                <>
                    {next.length === 0 ? (
                        <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No more dates this term.</p>
                    ) : (
                        // A calendar tile per date, so the name has the card's width
                        // (Jack, 27 Oct 2026: date and name side by side were crammed).
                        <ul className="list-plain m-0 flex flex-col divide-y divide-gray-100 p-0">
                            {next.map((d, i) => {
                                const day = calendarDay(d.date);
                                const moved = movedText(d);
                                return (
                                    <li key={d.key}>
                                        <button type="button" onClick={onAll} className={cn("flex w-full items-center gap-4 rounded-lg py-3 text-left", ROW_HOVER_TIGHT, FOCUS_RING)}>
                                            <DateChip date={day} next={i === 0} />
                                            <span className="min-w-0 flex-1">
                                                <span className="sr-only">{day ? shortDay(day) : "Date to be set"}: </span>
                                                <span className={cn(TYPE.tb, "block font-medium text-gray-900")}>{d.label}</span>
                                                <span className={cn(TYPE.tc, "block", moved ? "text-amber-700" : "text-gray-500")} aria-hidden={!moved}>
                                                    {moved ?? (day ? `${weekday(day)}${i === 0 ? ", next" : ""}` : "Date to be set")}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <p className={cn(TYPE.tc, "m-0 tabular-nums text-gray-500")}>
                        Month {cur} of {total} · {left === 1 ? "1 month left" : `${left} months left`}
                    </p>
                </>
            ) : (
                // Absent and null are the same answer: nothing has been published.
                // A started plan is waiting on the publish, not on the start (#4379).
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>
                    {started
                        ? "Your account manager publishes the dates for the whole term, and they appear here."
                        : "Once your plan starts we publish the dates for the whole term, and they appear here."}
                </p>
            )}
        </PhoneFoldCard>
    );
}

function ContactCard({
    payload,
    canAsk,
    replyNote,
    onAsk,
}: {
    payload: ClientPagePayload;
    canAsk: boolean;
    replyNote: ReplyNote | null;
    onAsk: () => void;
}) {
    const manager = payload.preparedBy?.trim() || null;
    const email = payload.contactEmail?.trim() || null;
    // Nothing to name, nowhere to write, nothing to press and no reply to point to: no card.
    if (!manager && !email && !canAsk && !replyNote) return null;
    return (
        <FoldCard id="contact" title={ACCOUNT_MANAGER_TITLE} icon={{ icon: UserRoundIcon, area: clientArea("people") }} foldedFact={manager ?? undefined}>
            {replyNote && <LinkedReplyNote notice={replyNote} manager={manager} email={email} className="mb-4" />}
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-3">
                <span aria-hidden className={cn(TYPE.tb, "grid h-9 w-9 shrink-0 place-items-center rounded-full font-semibold", ICON_SQUARE_TONE.blue)}>
                    {manager ? manager.charAt(0).toUpperCase() : <UserRoundIcon size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                    {/* Nobody assigned yet (the server sends null, #4808): ourteam, as everywhere else. */}
                    <p className={cn(TYPE.tb, "m-0 break-words font-medium text-gray-900")}>{manager ?? "ourteam"}</p>
                    {email && <p className={cn(TYPE.tc, "m-0 select-all break-all text-gray-500")}>{email}</p>}
                </div>
                {canAsk && (
                    <PillButton variant="primary" icon={MessageSquarePlusIcon} onClick={onAsk} className={cn("max-sm:h-11 max-sm:w-full sm:ml-auto", TAP_TARGET)}>
                        Ask for a change
                    </PillButton>
                )}
            </div>
        </FoldCard>
    );
}

